import { useAuth } from '../../../auth/AuthProvider';
import PropTypes from 'prop-types';
import { Alert, Box, Button, Paper, Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAgendaTranslation as useTranslation } from '../translations';
import dayjs from 'dayjs';
import { fetchAppointments } from '../../../services/appointments';
import { getAgendaBounds, groupAppointments } from '../agendaUtils';

const panelSx = {
    p: 2.5,
    border: '1px solid #DCE3E8',
    borderRadius: 2,
    boxShadow: 'none'
};

export const AgendaSummary = ({ dateRange, clinic, roleUuid, clinicUuid, zone }) => {
    const { user, role } = useAuth();
    const actor = { user, role };
    const { t, i18n } = useTranslation();
    const doctor = clinic ? undefined : roleUuid;
    const query = useQuery({
        queryKey: [
            'clinic-agenda-appointments',
            'summary',
            roleUuid,
            clinicUuid,
            zone,
            dateRange,
            doctor
        ],
        queryFn: ({ signal }) =>
            fetchAppointments(actor, {
                doctor,
                ...getAgendaBounds(dateRange, zone),
                order: 'ASC',
                allPages: true,
                signal
            }),
        enabled: Boolean(roleUuid),
        staleTime: 30000
    });
    const counts = { total: 0, SCHEDULED: 0, ONGOING: 0, COMPLETED: 0, CANCELLED: 0 };
    groupAppointments(query.data || [], '', dateRange, zone).forEach(
        ([, appointments]) => {
            appointments.forEach(({ status }) => {
                counts.total += 1;
                if (status !== 'total' && Object.hasOwn(counts, status))
                    counts[status] += 1;
            });
        }
    );
    const locale = (i18n?.resolvedLanguage || i18n?.language || 'pt').startsWith('pt')
        ? 'pt'
        : 'en';
    const start = dayjs(dateRange.start).locale(locale).format('D MMMM YYYY');
    const end = dayjs(dateRange.end).locale(locale).format('D MMMM YYYY');
    const rows = [
        ['total', '#757575', 'total'],
        ['scheduled', '#1976D2', 'SCHEDULED'],
        ['ongoing', '#FFB020', 'ONGOING'],
        ['completed', '#269B18', 'COMPLETED'],
        ['cancelled', '#FF5055', 'CANCELLED']
    ];
    return (
        <Paper
            component="section"
            aria-label={t('agenda.summary')}
            aria-busy={query.isFetching}
            elevation={0}
            sx={panelSx}
        >
            <Typography component="h2" sx={{ fontSize: '1.125rem', fontWeight: 700 }}>
                {t('agenda.summary')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                {dateRange.start === dateRange.end ? start : `${start} - ${end}`}
            </Typography>
            {query.isError && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button onClick={() => query.refetch()}>
                            {t('agenda.retry')}
                        </Button>
                    }
                >
                    {t('agenda.loadError')}
                </Alert>
            )}
            <Box sx={{ display: 'grid', gap: 1.5 }}>
                {rows.map(([label, color, status]) => (
                    <Box
                        key={label}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                        <Box
                            aria-hidden="true"
                            sx={{
                                width: 16,
                                height: 16,
                                flexShrink: 0,
                                borderRadius: '50%',
                                bgcolor: color
                            }}
                        />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                            {t(`agenda.${label}`)}
                        </Typography>
                        {query.isPending && query.fetchStatus !== 'idle' ? (
                            <Skeleton width={28} />
                        ) : (
                            <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{
                                    minWidth: 28,
                                    textAlign: 'right',
                                    fontVariantNumeric: 'tabular-nums'
                                }}
                            >
                                {query.isSuccess ? counts[status] : '-'}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>
        </Paper>
    );
};
AgendaSummary.propTypes = {
    clinic: PropTypes.bool.isRequired,
    roleUuid: PropTypes.string,
    clinicUuid: PropTypes.string,
    zone: PropTypes.string.isRequired,
    dateRange: PropTypes.shape({
        start: PropTypes.string.isRequired,
        end: PropTypes.string.isRequired
    }).isRequired
};
